// Extracted from CaseBuddy.vue in refactor pass 2.
// Shared case support, modal, dirty-state, sheet scaffold, and summary helpers.
// These are Options API method definitions and intentionally use `this`.

import {
  createCaseSheetState,
  createCaseWorkbookState,
  createDefaultCaseSupportState,
  createStorageSafeCaseSupportState,
  normalizeCaseSupportState
} from '../CaseBuddyCore.js';

export const caseBuddyBaseMethods = {
    clearCaseToastTimer() {
      if (this.caseToastTimer) {
        window.clearTimeout(this.caseToastTimer);
        this.caseToastTimer = null;
      }
    },

    dismissCaseToast() {
      this.clearCaseToastTimer();
      this.caseToast = '';
    },

    showCaseToast(message) {
      this.clearCaseToastTimer();
      this.caseToast = String(message || '').trim();
      if (!this.caseToast) return;

      this.caseToastTimer = window.setTimeout(() => {
        this.caseToast = '';
        this.caseToastTimer = null;
      }, 4200);
    },

    getCasePerformanceCache() {
      if (!this.__caseBuddyPerformanceCache) {
        Object.defineProperty(this, '__caseBuddyPerformanceCache', {
          value: {
            state: new Map(),
            view: new Map(),
            visual: new Map(),
            misc: new Map()
          },
          configurable: true
        });
      }
      return this.__caseBuddyPerformanceCache;
    },

    getCasePerformanceCacheVersion() {
      return Number(this.__caseBuddyPerformanceCacheVersion) || 0;
    },

    bumpCasePerformanceCacheVersion() {
      this.__caseBuddyPerformanceCacheVersion = this.getCasePerformanceCacheVersion() + 1;
      this.clearCasePerformanceCache();
      return this.__caseBuddyPerformanceCacheVersion;
    },

    clearCasePerformanceCache(namespace = '') {
      const cache = this.__caseBuddyPerformanceCache;
      if (!cache) return;
      if (namespace && cache[namespace]?.clear) {
        cache[namespace].clear();
        return;
      }
      Object.values(cache).forEach(store => store?.clear?.());
    },

    caseCacheFingerprint(value, fallback = '') {
      let text = '';
      try {
        text = JSON.stringify(value, (key, nextValue) => {
          if (typeof nextValue === 'function') return '[Function]';
          if (nextValue instanceof Map) return Array.from(nextValue.entries());
          if (nextValue instanceof Set) return Array.from(nextValue.values());
          return nextValue;
        });
      } catch {
        text = String(fallback || value || '');
      }
      text = String(text || fallback || '');
      let hash = 0;
      for (let index = 0; index < text.length; index += 1) {
        hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
      }
      return `${text.length}:${Math.abs(hash)}`;
    },

    getCaseComputationCacheKey(label = 'case', options = {}) {
      return [
        label,
        this.currentProjectFileId || '',
        Number(this.caseSupportState?.caseDataRevision) || 0,
        this.getCasePerformanceCacheVersion(),
        this.addressLegendLabelDormantSeparately ? 'dormant' : 'standard',
        this.addressLegendUseDepositLabels ? 'deposit-labels' : 'tool17d-labels',
        this.caseCacheFingerprint(options)
      ].join('|');
    },

    getCachedCaseComputation(namespace = 'state', key = '', producer = null) {
      if (typeof producer !== 'function') return undefined;
      const cache = this.getCasePerformanceCache();
      const store = cache[namespace] || cache.state || new Map();
      cache[namespace] = store;
      if (store.has(key)) {
        return store.get(key);
      }
      const value = producer();
      store.set(key, value);
      if (store.size > 96) {
        const firstKey = store.keys().next().value;
        if (firstKey !== undefined) store.delete(firstKey);
      }
      return value;
    },

    clearCaseInitialHydrationTimer() {
      if (this.casePageHydrationTimer) {
        window.clearTimeout(this.casePageHydrationTimer);
        this.casePageHydrationTimer = null;
      }
    },

    startCaseInitialHydration(options = {}) {
      const message = typeof options === 'string' ? options : options.message;
      this.clearCaseInitialHydrationTimer();
      this.casePageHydrating = true;
      this.casePageHydrationStartedAt = Date.now();
      this.casePageHydrationMessage = message || 'Loading case workspace…';
      this.casePageHydrationTimer = window.setTimeout(() => {
        this.casePageHydrationTimer = null;
        this.casePageHydrating = false;
        this.casePageHydrationMessage = 'Case workspace loaded.';
      }, Math.max(3000, Number(options.fallbackMs) || 9000));
    },

    completeCaseInitialHydrationIfReady(options = {}) {
      if (!this.casePageHydrating) {
        return true;
      }

      if (!this.projectFileReadyState?.input || !this.projectFileReadyState?.output) {
        if (this.projectFileReadyState?.input && !this.projectFileReadyState?.output) {
          this.casePageHydrationMessage = 'Input workbook is ready. Loading output review sheet…';
        } else if (!this.projectFileReadyState?.input && this.projectFileReadyState?.output) {
          this.casePageHydrationMessage = 'Output review sheet is ready. Loading input workbook…';
        } else {
          this.casePageHydrationMessage = options.waitingMessage || this.casePageHydrationMessage || 'Loading spreadsheet tabs…';
        }
        return false;
      }

      this.clearCaseInitialHydrationTimer();
      const delay = Math.max(0, Number(options.delay) || 0);
      const finish = () => {
        this.casePageHydrationTimer = null;
        this.casePageHydrating = false;
        this.casePageHydrationMessage = 'Case workspace loaded.';
        this.$nextTick(() => {
          this.$refs.inputSpreadsheet?.refreshDimensions?.();
          this.$refs.outputSpreadsheet?.refreshDimensions?.();
        });
      };

      if (delay > 0) {
        this.casePageHydrationMessage = options.readyMessage || 'Finalizing case workspace…';
        this.casePageHydrationTimer = window.setTimeout(finish, delay);
      } else {
        finish();
      }

      return true;
    },

    clearCaseGlobalOperationTimer() {
      if (this.caseGlobalOperationTimer) {
        window.clearTimeout(this.caseGlobalOperationTimer);
        this.caseGlobalOperationTimer = null;
      }
    },

    beginCaseGlobalOperation(options = {}) {
      this.clearCaseGlobalOperationTimer();
      const token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      this.caseGlobalOperation = {
        active: true,
        token,
        kind: options.kind || 'operation',
        title: options.title || 'Working…',
        message: options.message || 'Preparing…',
        detail: options.detail || '',
        progress: Math.max(0, Number(options.progress) || 0),
        total: Math.max(0, Number(options.total) || 0),
        startedAtUtc: new Date().toISOString(),
        finishedAtUtc: ''
      };
      return token;
    },

    updateCaseGlobalOperation(updates = {}, token = this.caseGlobalOperation?.token) {
      if (!this.caseGlobalOperation?.active || (token && this.caseGlobalOperation.token !== token)) {
        return false;
      }

      this.caseGlobalOperation = {
        ...this.caseGlobalOperation,
        ...updates,
        progress: updates.progress === undefined
          ? this.caseGlobalOperation.progress
          : Math.max(0, Number(updates.progress) || 0),
        total: updates.total === undefined
          ? this.caseGlobalOperation.total
          : Math.max(0, Number(updates.total) || 0)
      };
      return true;
    },

    finishCaseGlobalOperation(token = this.caseGlobalOperation?.token, options = {}) {
      if (!this.caseGlobalOperation?.active || (token && this.caseGlobalOperation.token !== token)) {
        return false;
      }

      const total = Number(this.caseGlobalOperation.total) || 0;
      this.caseGlobalOperation = {
        ...this.caseGlobalOperation,
        message: options.message || this.caseGlobalOperation.message || 'Complete.',
        detail: options.detail || '',
        progress: total > 0 ? total : this.caseGlobalOperation.progress,
        finishedAtUtc: new Date().toISOString()
      };

      this.clearCaseGlobalOperationTimer();
      this.caseGlobalOperationTimer = window.setTimeout(() => {
        if (this.caseGlobalOperation?.token === token) {
          this.caseGlobalOperation = {
            active: false,
            token: '',
            kind: '',
            title: '',
            message: '',
            detail: '',
            progress: 0,
            total: 0,
            startedAtUtc: '',
            finishedAtUtc: ''
          };
        }
        this.caseGlobalOperationTimer = null;
      }, Math.max(0, Number(options.hideDelayMs) || 650));
      return true;
    },

    clearCaseModalOperationTimer() {
      if (this.caseModalOperationTimer) {
        window.clearTimeout(this.caseModalOperationTimer);
        this.caseModalOperationTimer = null;
      }
    },

    beginCaseModalOperation(options = {}) {
      if (this.caseModalOperation?.active && !options.replaceActive) {
        return this.caseModalOperation.token || '';
      }

      this.clearCaseModalOperationTimer();
      const token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      this.caseModalOperation = {
        active: true,
        token,
        kind: options.kind || 'modal-operation',
        title: options.title || 'Preparing view…',
        message: options.message || 'Building the latest case preview…',
        detail: options.detail || '',
        target: options.target || '',
        startedAtUtc: new Date().toISOString(),
        finishedAtUtc: ''
      };
      return token;
    },

    updateCaseModalOperation(updates = {}, token = this.caseModalOperation?.token) {
      if (!this.caseModalOperation?.active || (token && this.caseModalOperation.token !== token)) {
        return false;
      }

      this.caseModalOperation = {
        ...this.caseModalOperation,
        ...updates
      };
      return true;
    },

    finishCaseModalOperation(token = this.caseModalOperation?.token, options = {}) {
      if (!this.caseModalOperation?.active || (token && this.caseModalOperation.token !== token)) {
        return false;
      }

      this.caseModalOperation = {
        ...this.caseModalOperation,
        message: options.message || this.caseModalOperation.message || 'Ready.',
        detail: options.detail || '',
        finishedAtUtc: new Date().toISOString()
      };

      this.clearCaseModalOperationTimer();
      this.caseModalOperationTimer = window.setTimeout(() => {
        if (this.caseModalOperation?.token === token) {
          this.caseModalOperation = {
            active: false,
            token: '',
            kind: '',
            title: '',
            message: '',
            detail: '',
            target: '',
            startedAtUtc: '',
            finishedAtUtc: ''
          };
        }
        this.caseModalOperationTimer = null;
      }, Math.max(0, Number(options.hideDelayMs) || 480));
      return true;
    },

    async runCaseModalOperation(options = {}, task = null) {
      if (this.caseModalOperation?.active && !options.replaceActive) {
        return false;
      }

      const token = this.beginCaseModalOperation(options);
      await this.yieldToCaseUi({ paint: true, frames: 2, minDelayMs: 24 });
      try {
        if (options.stepMessage || options.stepDetail) {
          this.updateCaseModalOperation({
            message: options.stepMessage || this.caseModalOperation.message,
            detail: options.stepDetail || this.caseModalOperation.detail
          }, token);
          await this.yieldToCaseUi({ paint: true, minDelayMs: 8 });
        }
        const result = typeof task === 'function' ? await task(token) : true;
        this.finishCaseModalOperation(token, {
          message: options.finishMessage || 'Ready.',
          hideDelayMs: options.hideDelayMs
        });
        return result;
      } catch (error) {
        this.finishCaseModalOperation(token, {
          message: options.errorMessage || 'Something went wrong while preparing this view.',
          detail: error?.message || '',
          hideDelayMs: Math.max(900, Number(options.hideDelayMs) || 0)
        });
        throw error;
      }
    },

    async runCaseModalMethod(methodName, operationOptions = {}, ...args) {
      const name = String(methodName || '').trim();
      const method = name ? this[name] : null;
      if (typeof method !== 'function') {
        return false;
      }

      const result = await this.runCaseModalOperation({
        title: operationOptions.title || 'Updating review state…',
        message: operationOptions.message || 'Saving the review confirmation and refreshing status…',
        detail: operationOptions.detail || '',
        target: operationOptions.target || name,
        finishMessage: operationOptions.finishMessage || 'Review state updated.',
        hideDelayMs: operationOptions.hideDelayMs
      }, async () => method.apply(this, args));

      await this.maybeAutoCloseCompletedReviewModal(name, result, operationOptions);
      return result;
    },

    caseReviewCompletionModalCloseMap() {
      return {
        markCaseInconsistencyReviewComplete: { show: 'showCaseInconsistencyModal', close: 'cancelCaseInconsistencyReviewModal' },
        markDepositHotWalletReviewComplete: { show: 'showDepositHotWalletModal', close: 'cancelDepositHotWalletModal' },
        dismissRemainingDepositPatternCandidatesAndComplete: { show: 'showDepositPatternModal', close: 'cancelDepositPatternReviewModal' },
        markDepositPatternReviewComplete: { show: 'showDepositPatternModal', close: 'cancelDepositPatternReviewModal' },
        dismissRemainingKnownEntityCasingCandidatesAndComplete: { show: 'showKnownEntityCasingModal', close: 'cancelKnownEntityCasingReviewModal' },
        markKnownEntityCasingReviewComplete: { show: 'showKnownEntityCasingModal', close: 'cancelKnownEntityCasingReviewModal' },
        markDuplicateHashLabelerReviewComplete: { show: 'showDuplicateHashLabelerModal', close: 'cancelDuplicateHashLabelerModal' },
        markNoteHashRewriteReviewComplete: { show: 'showNoteHashRewriteModal', close: 'cancelNoteHashRewriteModal' },
        markWwwUsdReferenceReviewComplete: { show: 'showWwwUsdReferenceModal', close: 'cancelWwwUsdReferenceReviewModal' },
        markAddressLegendReviewComplete: { show: 'showAddressLegendModal', close: 'cancelAddressLegendModal' },
        markTemplateDeliverableReviewComplete: { show: 'showTemplateDeliverableModal', close: 'cancelTemplateDeliverableModal' },
        markSuspiciousAddressTableReviewComplete: { show: 'showSuspiciousAddressTableModal', close: 'cancelSuspiciousAddressTableModal' },
        markExchangeAddressTableReviewComplete: { show: 'showExchangeAddressTableModal', close: 'cancelExchangeAddressTableModal' },
        markDormantAddressDeliverablesReviewComplete: { show: 'showDormantAddressDeliverablesModal', close: 'cancelDormantAddressDeliverablesModal' },
        markWhatWentWhereReviewComplete: { show: 'showWhatWentWhereModal', close: 'cancelWhatWentWhereModal' },
        markCaseConclusionReviewComplete: { show: 'showCaseConclusionModal', close: 'cancelCaseConclusionModal' },
        markReportBodyReviewComplete: { show: 'showReportBodyModal', close: 'cancelReportBodyModal' },
        markAddendumPathEndingReviewComplete: { show: 'showAddendumPathEndingReviewModal', close: 'cancelAddendumPathEndingReviewModal' },
        markAddendumRecipientEntityReviewComplete: { show: 'showAddendumRecipientEntityModal', close: 'cancelAddendumRecipientEntityModal' },
        markCaseAddendumReviewComplete: { show: 'showCaseAddendumModal', close: 'cancelCaseAddendumModal' },
        markCaseFlowVisualsDone: { show: 'showCaseFlowVisualsModal', close: 'cancelCaseFlowVisualsModal' },
        markCaseFullReportPackageReviewComplete: { show: 'showCaseFullReportPackageModal', close: 'cancelCaseFullReportPackageModal' },
        markAttributionPathOperationsCompleteFromModal: { show: 'showPathOperationModal', close: 'cancelPathOperationModal' },
        markEntityCuratorReviewCompleteFromModal: { show: 'showEntityCuratorModal', close: 'cancelEntityCuratorReviewModal' }
      };
    },

    caseModalMethodSucceeded(result) {
      if (result === false || result === null) return false;
      if (result && typeof result === 'object' && result.success === false) return false;
      return true;
    },

    async maybeAutoCloseCompletedReviewModal(methodName, result, operationOptions = {}) {
      if (!this.caseModalMethodSucceeded(result)) {
        return false;
      }

      if (operationOptions.autoCloseOnSuccess === false) {
        return false;
      }

      if (this.caseAutoCloseCompletedReviewModals === false && operationOptions.autoCloseOnSuccess !== true) {
        return false;
      }

      const map = this.caseReviewCompletionModalCloseMap();
      const config = map[String(methodName || '').trim()];
      if (!config || (config.show && !this[config.show]) || typeof this[config.close] !== 'function') {
        return false;
      }

      this.updateCaseModalOperation?.({
        message: operationOptions.closeMessage || 'Review saved. Closing this panel…'
      });
      await this.yieldToCaseUi({ paint: true, frames: 1, minDelayMs: 120 });
      this[config.close]();
      return true;
    },

    yieldToCaseUi(options = {}) {
      const paint = options === false ? false : (options === true || options.paint !== false);
      const requestedFrames = Math.max(1, Math.min(4, Number(options.frames) || (paint ? 2 : 1)));
      const minDelayMs = Math.max(0, Number(options.minDelayMs) || 0);

      return new Promise((resolve) => {
        const finishAfterDelay = () => {
          if (minDelayMs > 0) {
            window.setTimeout(resolve, minDelayMs);
            return;
          }
          resolve();
        };

        const afterVueFlush = () => {
          if (typeof window.requestAnimationFrame !== 'function') {
            window.setTimeout(finishAfterDelay, 0);
            return;
          }

          let remainingFrames = requestedFrames;
          const waitFrame = () => {
            window.requestAnimationFrame(() => {
              remainingFrames -= 1;
              if (remainingFrames > 0) {
                waitFrame();
                return;
              }

              if (paint) {
                window.setTimeout(finishAfterDelay, 0);
                return;
              }
              finishAfterDelay();
            });
          };
          waitFrame();
        };

        if (typeof this.$nextTick === 'function') {
          this.$nextTick(afterVueFlush);
          return;
        }
        afterVueFlush();
      });
    },

    shouldRunCaseOperationWrapper(options = {}) {
      return !options.silent &&
        !options.__caseOperationWrapped &&
        !this.caseModalOperation?.active &&
        !this.caseGlobalOperation?.active;
    },

    clearCaseSummaryRefreshTimer() {
      if (this.caseSummaryRefreshTimer) {
        window.clearTimeout(this.caseSummaryRefreshTimer);
        this.caseSummaryRefreshTimer = null;
      }
      if (this.caseSummaryRefreshIdleHandle && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.caseSummaryRefreshIdleHandle);
        this.caseSummaryRefreshIdleHandle = null;
      }
    },

    queueCaseSummaryRefresh(delay = 160, options = {}) {
      this.clearCaseSummaryRefreshTimer();

      const runRefresh = () => {
        this.caseSummaryRefreshTimer = null;
        this.caseSummaryRefreshIdleHandle = null;
        this.refreshCaseSummary({ light: !!options.light });
      };

      this.caseSummaryRefreshTimer = window.setTimeout(() => {
        this.caseSummaryRefreshTimer = null;
        if (options.idle && typeof window.requestIdleCallback === 'function') {
          this.caseSummaryRefreshIdleHandle = window.requestIdleCallback(runRefresh, {
            timeout: Number(options.timeout) || 3500
          });
          return;
        }
        runRefresh();
      }, delay);
    },

    clearCaseSupportStatePersistTimer() {
      if (this.caseSupportPersistTimer) {
        window.clearTimeout(this.caseSupportPersistTimer);
        this.caseSupportPersistTimer = null;
      }
    },

    queueCaseSupportStatePersist(delay = 300, fileId = this.currentProjectFileId) {
      this.clearCaseSupportStatePersistTimer();
      this.caseSupportPersistTimer = window.setTimeout(() => {
        this.caseSupportPersistTimer = null;
        this.persistCaseSupportState(fileId);
      }, delay);
    },

    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (!element) return;
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    isCaseBuddyStorageQuotaError(error = {}) {
      const name = String(error?.name || '');
      const message = String(error?.message || '');
      return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || /quota|storage/i.test(message);
    },

    formatCaseBuddyStorageBytes(bytes = 0) {
      const value = Math.max(0, Number(bytes) || 0);
      if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
      if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${Math.round(value)} B`;
    },

    estimateCaseBuddyLocalStorageUsage() {
      const estimate = {
        supported: false,
        totalBytes: 0,
        caseBuddyBytes: 0,
        caseBuddyProjectBytes: 0,
        caseBuddyFileCount: 0,
        itemCount: 0,
        largestItems: []
      };

      try {
        if (typeof localStorage === 'undefined') return estimate;
        estimate.supported = true;
        const items = [];
        for (let index = 0; index < localStorage.length; index += 1) {
          const key = localStorage.key(index) || '';
          const value = localStorage.getItem(key) || '';
          const bytes = (key.length + value.length) * 2;
          estimate.itemCount += 1;
          estimate.totalBytes += bytes;
          if (key.startsWith('tool60_') || key.includes('case_buddy')) {
            estimate.caseBuddyBytes += bytes;
          }
          if (key.startsWith('tool60_spreadsheet_project_')) {
            estimate.caseBuddyProjectBytes += bytes;
            estimate.caseBuddyFileCount += 1;
          }
          items.push({ key, bytes });
        }
        estimate.largestItems = items
          .sort((left, right) => right.bytes - left.bytes)
          .slice(0, 8)
          .map(item => ({
            key: item.key,
            bytes: item.bytes,
            displaySize: this.formatCaseBuddyStorageBytes(item.bytes)
          }));
      } catch {
        // Best-effort storage estimate only.
      }

      return estimate;
    },

    setCaseProjectStorageNotice(details = {}) {
      const estimate = details.storageEstimate || this.estimateCaseBuddyLocalStorageUsage();
      const attemptedBytes = Math.max(0, Number(details.bytes) || 0);
      const message = details.message || (details.quota
        ? 'Browser storage is full. Delete older saved case files, then try saving or creating a case again.'
        : 'Case Buddy could not write this case to browser storage.');
      const notice = {
        visible: true,
        quota: !!details.quota,
        title: details.title || (details.quota ? 'Browser storage is full' : 'Case storage needs attention'),
        message,
        storageKey: details.storageKey || '',
        errorName: details.errorName || '',
        attemptedBytes,
        attemptedSizeLabel: attemptedBytes ? this.formatCaseBuddyStorageBytes(attemptedBytes) : '',
        totalBytes: estimate.totalBytes || 0,
        totalSizeLabel: this.formatCaseBuddyStorageBytes(estimate.totalBytes || 0),
        caseBuddyBytes: estimate.caseBuddyBytes || 0,
        caseBuddySizeLabel: this.formatCaseBuddyStorageBytes(estimate.caseBuddyBytes || 0),
        caseBuddyProjectBytes: estimate.caseBuddyProjectBytes || 0,
        caseBuddyProjectSizeLabel: this.formatCaseBuddyStorageBytes(estimate.caseBuddyProjectBytes || 0),
        itemCount: estimate.itemCount || 0,
        caseBuddyFileCount: estimate.caseBuddyFileCount || 0,
        largestItems: Array.isArray(estimate.largestItems) ? estimate.largestItems : [],
        updatedAtUtc: new Date().toISOString()
      };
      this.caseProjectStorageNotice = notice;
      return notice;
    },

    clearCaseProjectStorageNotice() {
      this.caseProjectStorageNotice = {
        visible: false,
        quota: false,
        title: '',
        message: '',
        storageKey: '',
        errorName: '',
        attemptedBytes: 0,
        attemptedSizeLabel: '',
        totalBytes: 0,
        totalSizeLabel: '',
        caseBuddyBytes: 0,
        caseBuddySizeLabel: '',
        caseBuddyProjectBytes: 0,
        caseBuddyProjectSizeLabel: '',
        itemCount: 0,
        caseBuddyFileCount: 0,
        largestItems: [],
        updatedAtUtc: ''
      };
    },

    handleProjectWorkbookStorageError(result = {}) {
      const notice = this.setCaseProjectStorageNotice({
        ...result,
        quota: result.quota !== false,
        title: result.quota === false ? 'Workbook save failed' : 'Browser storage is full',
        message: result.message || 'Case Buddy could not save the input workbook to browser storage.'
      });
      this.showCaseToast?.(`${notice.title}. Open Browse cases to delete older saved cases.`);
      return notice;
    },

    safeCaseBuddyLocalStorageSet(storageKey = '', value = '', options = {}) {
      const key = String(storageKey || '');
      if (!key) return { ok: false, storageKey: '', quota: false, message: 'No storage key was provided.' };
      const serialized = typeof value === 'string' ? value : JSON.stringify(value ?? '');
      try {
        localStorage.setItem(key, serialized);
        return {
          ok: true,
          storageKey: key,
          bytes: serialized.length * 2,
          quota: false,
          message: ''
        };
      } catch (error) {
        const result = {
          ok: false,
          storageKey: key,
          bytes: serialized.length * 2,
          quota: this.isCaseBuddyStorageQuotaError(error),
          errorName: String(error?.name || 'StorageError'),
          message: error?.message || 'Could not write to browser storage.',
          storageEstimate: this.estimateCaseBuddyLocalStorageUsage()
        };
        this.setCaseProjectStorageNotice(result);
        if (!options.silent) {
          this.showCaseToast?.(result.quota
            ? 'Browser storage is full. Delete older saved cases from Browse cases, then save again.'
            : 'Could not save this case to browser storage.');
        }
        console.warn('Case Buddy localStorage write failed.', result, error);
        return result;
      }
    },

    safeCaseBuddyLocalStorageRemove(storageKey = '') {
      const key = String(storageKey || '');
      if (!key) return false;
      try {
        localStorage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },

    getCaseSupportStorageKey(fileId = this.currentProjectFileId) {
      return fileId ? `tool60_spreadsheet_project_${fileId}_case_support_v1` : '';
    },

    loadCaseSupportState(fileId = this.currentProjectFileId) {
      this.bumpCasePerformanceCacheVersion();
      const storageKey = this.getCaseSupportStorageKey(fileId);
      if (!storageKey) {
        this.caseSupportState = createDefaultCaseSupportState();
        this.caseSupportStateLastPersistedJson = '';
        return this.caseSupportState;
      }

      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        this.caseSupportState = createDefaultCaseSupportState();
        this.caseSupportStateLastPersistedJson = '';
        this.persistCaseSupportState();
        return this.caseSupportState;
      }

      try {
        this.caseSupportState = normalizeCaseSupportState(JSON.parse(raw));
      } catch (error) {
        console.warn('Failed to parse Case Buddy support state.', error);
        this.caseSupportState = createDefaultCaseSupportState();
      }

      try {
        this.caseSupportStateLastPersistedJson = JSON.stringify(createStorageSafeCaseSupportState(this.caseSupportState));
      } catch {
        this.caseSupportStateLastPersistedJson = raw || '';
      }

      return this.caseSupportState;
    },

    persistCaseSupportState(fileId = this.currentProjectFileId) {
      this.clearCaseSupportStatePersistTimer();
      const storageKey = this.getCaseSupportStorageKey(fileId);
      if (!storageKey) return null;

      const normalized = normalizeCaseSupportState(this.caseSupportState);
      this.caseSupportState = normalized;
      const serialized = JSON.stringify(createStorageSafeCaseSupportState(normalized));
      if (serialized !== this.caseSupportStateLastPersistedJson) {
        const saveResult = this.safeCaseBuddyLocalStorageSet(storageKey, serialized, { silent: true });
        if (saveResult.ok) {
          this.caseSupportStateLastPersistedJson = serialized;
          this.bumpCasePerformanceCacheVersion();
        }
      }
      return normalized;
    },

    createDefaultCaseStorageForFile(fileId) {
      if (!fileId) return;

      const inputKey = this.getProjectInputStorageKey(fileId);
      const outputKey = this.getProjectOutputStorageKey(fileId);
      const supportKey = this.getCaseSupportStorageKey(fileId);

      const results = [];

      if (!localStorage.getItem(inputKey)) {
        results.push(this.safeCaseBuddyLocalStorageSet(inputKey, JSON.stringify(createCaseWorkbookState())));
      }

      if (!localStorage.getItem(outputKey)) {
        results.push(this.safeCaseBuddyLocalStorageSet(outputKey, JSON.stringify(createCaseSheetState({ includeHeaders: false }))));
      }

      if (!localStorage.getItem(supportKey)) {
        results.push(this.safeCaseBuddyLocalStorageSet(supportKey, JSON.stringify(createDefaultCaseSupportState())));
      }

      const failed = results.filter(result => result && result.ok === false);
      return {
        ok: failed.length === 0,
        failed,
        results
      };
    },

    cloneCurrentCaseSupportStateToFile(fileId) {
      if (!fileId) return;
      const clone = normalizeCaseSupportState(JSON.parse(JSON.stringify(this.caseSupportState || createDefaultCaseSupportState())));
      clone.dirty = false;
      clone.dirtySinceUtc = '';
      clone.lastSavedRevision = clone.caseDataRevision;
      clone.lastSavedUtc = new Date().toISOString();
      return this.safeCaseBuddyLocalStorageSet(this.getCaseSupportStorageKey(fileId), JSON.stringify(clone));
    },

    openStartingMainTransactionsModal() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.startingMainTransactionsPaste = '';
      this.startingMainTransactionDraftRows = [];
      this.showStartingMainTransactionsModal = true;
      this.$nextTick(() => this.refreshStartingMainTransactionStagedStatuses());
    },

    cancelStartingMainTransactionsModal() {
      this.showStartingMainTransactionsModal = false;
      if (this.caseSimpleLaunchReturnAfterStartingModal) {
        this.caseSimpleLaunchReturnAfterStartingModal = false;
        this.restoreCaseSimpleLaunch?.();
        this.caseSimpleLaunchStatus = 'Starting-transaction loading was closed. Load rows or run Auto when ready.';
      }
    },

    async openNetworkLookupModal() {
      return this.runCaseModalOperation({
        title: 'Opening network discovery…',
        message: 'Preparing transaction rows for network lookup.',
        target: 'network-lookup-modal',
        finishMessage: 'Network lookup view ready.'
      }, async () => {
        if (!this.authSessionLoaded) {
          await this.loadAuthSession();
        }
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showNetworkLookupModal = true;
        this.networkLookupStatus = '';
        await this.yieldToCaseUi();
        this.refreshNetworkLookupRows();
        return true;
      });
    },

    cancelNetworkLookupModal() {
      if (this.networkLookupRunning) return;
      this.showNetworkLookupModal = false;
    },

    async openDateLookupModal() {
      return this.runCaseModalOperation({
        title: 'Opening date lookup…',
        message: 'Preparing transaction rows for UTC date completion.',
        target: 'date-lookup-modal',
        finishMessage: 'Date lookup view ready.'
      }, async () => {
        if (!this.authSessionLoaded) {
          await this.loadAuthSession();
        }
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showDateLookupModal = true;
        this.dateLookupStatus = '';
        await this.yieldToCaseUi();
        this.refreshDateLookupRows();
        return true;
      });
    },

    cancelDateLookupModal() {
      if (this.dateLookupRunning) return;
      this.showDateLookupModal = false;
    }
};
